import { useQuery } from '@tanstack/react-query'
import { BatteryCharging, Loader2, MapPin, Square, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { driver, useAction, useActive } from '@/api/driver'
import type { DriverSession } from '@/api/types'
import { EmptyState, ErrorState, LoadingBlock, Stat } from '@/components/common'
import { Button, LinkButton } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/overlay'
import { dateTime, duration, kw, npr } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { session } from '@/lib/session'
import { STARTING_KEY } from './ChargePage'
import { Receipt } from './HistoryPage'

/** A start counts as "waiting for the charger" for this long. */
const START_WINDOW = 90_000

function useStarting(hasActive: boolean) {
  const read = () => {
    try {
      const v = JSON.parse(sessionStorage.getItem(STARTING_KEY) ?? 'null') as { chargerId: string; at: number; userId?: number } | null
      return v && v.userId === session.get()?.user.id && v.userId != null && Date.now() - v.at < START_WINDOW ? v : null
    } catch {
      return null
    }
  }
  const [starting, setStarting] = useState(read)
  useEffect(() => {
    if (hasActive) {
      try {
        sessionStorage.removeItem(STARTING_KEY)
      } catch {
        /* ignore */
      }
    }
    const id = window.setInterval(() => setStarting(read()), 3000)
    return () => window.clearInterval(id)
  }, [hasActive])
  return hasActive ? null : starting
}

export function ChargingPage() {
  const { t } = useI18n()
  const active = useActive()
  const sessions = active.data?.active_sessions ?? []
  const starting = useStarting(sessions.length > 0)
  const last = useQuery({ queryKey: ['history', 'last'], queryFn: () => driver.history({ page: 1, per_page: 1 }), enabled: !active.isLoading && sessions.length === 0 && !starting })

  if (active.isLoading) return <LoadingBlock rows={3} />
  if (active.error) return <ErrorState error={active.error} onRetry={() => active.refetch()} />

  if (sessions.length) return <div className="space-y-4">{sessions.map((s) => <LiveSession key={s.id} s={s} />)}</div>

  if (starting)
    return (
      <Card className="mx-auto max-w-lg p-6 text-center">
        <Loader2 className="mx-auto size-8 animate-spin text-brand" />
        <p className="mt-3 font-semibold">{t('Waiting for the charger')}</p>
        <p className="mt-1 text-sm text-text-2">{t('{id} accepted the request. Charging shows here as soon as it begins. Make sure the cable is plugged into your car.', { id: starting.chargerId })}</p>
      </Card>
    )

  const prev = last.data?.sessions[0]
  return (
    <div className="mx-auto max-w-lg">
      <EmptyState icon={<Zap />} title={t('Not charging')} body={t('Find a free charger on the map and press Start.')} action={<LinkButton to="/" variant="brand"><MapPin /> {t('Find a charger')}</LinkButton>} />
      {prev && prev.status !== 'active' && (
        <>
          <h2 className="mb-2 mt-2 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Your last charge')}</h2>
          <Card className="p-4">
            <Receipt s={prev} />
            <div className="mt-4 grid grid-cols-2 gap-2">
              <LinkButton to={`/receipts/${prev.id}`} variant="outline">{t('Open receipt')}</LinkButton>
              <LinkButton to={`/receipts/${prev.id}`} variant="outline">{t('Rate this station')}</LinkButton>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}

function LiveSession({ s }: { s: DriverSession }) {
  const { t } = useI18n()
  const [confirm, setConfirm] = useState(false)
  const stop = useAction(() => driver.stop(s.id), { success: t('Stop sent. The charger finishes in a few seconds.'), invalidate: [['active'], ['balance'], ['history']] })
  const soc = s.soc ?? null
  const [, tick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 30_000)
    return () => window.clearInterval(id)
  }, [])

  return (
    <Card className="mx-auto max-w-lg overflow-hidden">
      <div className="bg-ink p-5 text-white">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-sm font-medium text-brand"><span className="pulse-dot size-2 rounded-full bg-brand" /> {s.is_interrupted ? t('Connection to the charger lost') : t('Charging')}</p>
          <p className="text-xs text-white/60">{t('Started {at}', { at: dateTime(s.start_time) })}</p>
        </div>
        <p className="mt-1 truncate text-sm text-white/70">{`${s.station_name ?? ''} · ${s.charger_id} #${s.connector_id}${s.connector?.connector_type ? ` ${s.connector.connector_type}` : ''}`}</p>
        <div className="mt-5 flex items-end justify-between gap-4">
          <div>
            <p className="tabular text-4xl font-bold">{s.energy_kwh.toFixed(2)}<span className="ml-1 text-lg font-medium text-white/70">kWh</span></p>
            <p className="tabular mt-1 text-xl font-semibold text-brand">{npr(s.cost)}</p>
          </div>
          {soc != null && (
            <div className="text-right">
              <BatteryCharging className="ml-auto size-6 text-brand" />
              <p className="tabular text-2xl font-bold">{Math.round(soc)}%</p>
            </div>
          )}
        </div>
        {soc != null && (
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/15">
            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.min(100, soc)}%` }} />
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 p-5">
        <Stat label={t('Power')} value={kw(s.current_power_kw)} />
        <Stat label={t('Time')} value={duration(s.start_time)} />
        <Stat label={t('Rate')} value={s.cost_breakdown?.rate_per_kwh != null ? `${npr(s.cost_breakdown.rate_per_kwh)}` : '–'} />
      </div>
      <p className="px-5 text-xs text-text-2">{t('The charger reports energy about every 30 seconds; this page updates every 5.')}</p>
      <div className="p-5">
        <Button variant="danger" size="lg" className="h-12 w-full text-base" loading={stop.isPending} onClick={() => setConfirm(true)}>
          <Square className="fill-current" /> {t('Stop charging')}
        </Button>
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={t('Stop charging?')} body={t('You pay for the energy used so far. Unplug the cable after the charger finishes.')} confirmLabel={t('Stop charging')} danger onConfirm={() => stop.mutateAsync(undefined)} />
    </Card>
  )
}
